import UserManagementPage from './user-management';
import { TextField, UserField, EmailField, LabelField, ComboField, DateField } from 'components/ui/DetailForm';
import { IApplicationModule } from 'components/ui/IApplicationModule';
import { IUserSearchResult, AdminUserModel, UserStatusEnum, EmployeeTypeEnum } from '../../../shared/models/api/models';
import { ReactComponent as UserIcon } from "assets/icons/users-filled.svg";
import { ReactComponent as MakeInactiveIcon } from "assets/icons/make-inactive.svg";
import { ReactComponent as MakeActiveIcon } from "assets/icons/make-active.svg";
import { stringSort, dateSort } from 'helpers/HelperFunctions';
import { BarChartOutlined, Create, EditOutlined, Info, RefreshOutlined } from '@mui/icons-material';

// we could probably move these to the toolbar as standard text buttons
export const ViewOrEditButtonText = "View/Edit";
export const MakeInactiveButtonText = "Make inactive";
export const MakeActiveButtonText = "Make active";
export const RefreshButtonText = "Refresh";

const isContractor = (user: AdminUserModel) => {
    return user.employeeType === EmployeeTypeEnum.Contractor;
};
const isEmployee = (user: AdminUserModel) => {
    return user.employeeType === EmployeeTypeEnum.Employee;
}

const byEmail = (a: AdminUserModel, b: AdminUserModel, order: number) => {
    return stringSort(a.email, b.email) * order;
};

const byUsername = (a: AdminUserModel, b: AdminUserModel, order: number) => {
    return stringSort(a.username, b.username) * order;
};

const byLastLogin = (a: AdminUserModel, b: AdminUserModel, order: number) => {
    return dateSort(a.lastLoginDate, b.lastLoginDate) * order;
};

const byName = (a: AdminUserModel, b: AdminUserModel, order: number) => {
    return stringSort(`${a.firstname} ${a.lastname}`, `${b.firstname} ${b.lastname}`) * order;
};

const byMobile = (a: AdminUserModel, b: AdminUserModel, order: number) => {
    return stringSort(a.mobile, b.mobile) * order;
};

export const EmployeeTypeEmployeeLabel = "BHP User";
export const EmployeeTypeContractorLabel = "Contractor User";

export const UserManagementModule = {
    title: "User Management",
    className: "user-management",
    url: "/administration/user-management",
    icon: UserIcon,
    permission: "web.admin.user.read",
    component: UserManagementPage,
    selectMode: "many",    
    doubleClickAction: ViewOrEditButtonText,
    actions: [
        {
            name: ViewOrEditButtonText,
            icon: EditOutlined,
            permission: "web.admin.user.read",
            minSelectedCount: 1,
            maxSelectedCount: 1,
            openView: "view"
        },
        {
            name: MakeInactiveButtonText,
            icon: MakeInactiveIcon,
            permission: "web.admin.user.make-inactive"
        },
        {
            name: MakeActiveButtonText,
            icon: MakeActiveIcon,
            permission: "web.admin.user.make-inactive"
        }
    ],
    views: [
        {
            name: "view",
            mode: "view",
            icon: Info,
            heading: "User Information",
            subSubHeading: (usr: AdminUserModel) => ("User Information: " + usr.firstname + " " + usr.lastname),
            subSubIcon: BarChartOutlined,
            defaultAction: "edit",
            actions: [
                { name: "cancel", label: "Close", setResult: "close" },
                { name: "edit", label: "Edit", switchToView: "edit", permission: "web.admin.user.edit" }
            ]
        },
        {
            name: "edit",
            mode: "edit",
            heading: "User details",
            icon: Create,
            subHeading: "Edit user details",
            subSubIcon: BarChartOutlined,
            subSubHeading: (usr: AdminUserModel) => ("User Information: " + usr.firstname + " " + usr.lastname),
            editLayout: "grid",
            defaultAction: "save",
            actions: [
                { name: "cancel", label: "Cancel", setResult: "close" },
                { name: "save", label: "Update details", setResult: "save", validate: true }
            ]
        }
    ],
    columns: [
        {
            name: "id",
            heading: "id",
            className: "number-col"
        },
        {
            name: "username",
            heading: "Username",
            sort: byUsername,
            readonly: true,
            className: "string-col"
        },
        {
            name: "lastLoginDate",
            heading: "Last login date and time",
            gridHeading: "Last login",
            showInGrid: 4,
            className: "date-col",
            formComponent: DateField,
            readonly: true,
            sort: byLastLogin,
            format: (val?: string) => {
                return (!val ? null : new Date(val).toLocaleDateString())
            }
        },
        {
            name: "firstname",
            heading: "First name",
            className: "string-col",
            formComponent: TextField,
            readonlyIf: isEmployee,
            validation: {
                required: true,
                minLength: 2,
                tip: "Enter first name"
            },
            placeholderText: "Enter first name",
            setValue: (user: AdminUserModel, val: string) => {
                user.firstname = val;
                user.fullname = user.firstname + ' ' + user.lastname;
            }
        },
        {
            name: "lastname",
            heading: "Last name",
            className: "string-col",
            formComponent: TextField,
            readonlyIf: isEmployee,
            validation: {
                required: true,
                minLength: 2,
                tip: "Enter last name"
            },
            placeholderText: "Enter last name",
            setValue: (user: AdminUserModel, val: string) => {
                user.lastname = val;
                user.fullname = user.firstname + ' ' + user.lastname;
            }
        },
        {
            name: "fullname",
            heading: "Name",
            className: "string-col",
            showInGrid: 1,
            sort: byName,
            clickAction: ViewOrEditButtonText,
            readonly: true,
            readonlyForEdit: true
        },
        {
            name: "email",
            heading: "Email address",
            sort: byEmail,
            readonly: true,
            className: "email-col",
            showInGrid: 2,            
            formComponent: EmailField
        },
        {
            name: "mobile",
            heading: "Phone number",
            showInGrid: 13,
            className: "phone-col",
            readonlyIf: isEmployee,
            sort: byMobile,
            formComponent: TextField,
            validation: {
                required: true,
                segments: { splitter: ' ', min: 3 },
                minLength: 8,
                tip: "Phone number is mandatory"
            },
            placeholderText: "(+61) 412 123 123",
            setValue: (user: AdminUserModel, val: string) => { user.mobile = val; }
        },
        {
            name: "password",
            heading: "Password",
            className: "password-col",
            readonly: true,
            formComponent: LabelField,
            formatRow: (row) => {
                // IKR!
                return "*********";
            }
        },
        {
            name: "employeeType",
            heading: "Employee type",
            className: "string-col",
            formComponent: ComboField,
            showInGrid: 5,
            filterable: true,
            readonly: true,
            validation: {
                values: [
                    { key: EmployeeTypeEnum.Contractor, value: EmployeeTypeContractorLabel },
                    { key: EmployeeTypeEnum.Employee, value: EmployeeTypeEmployeeLabel },
                    { key: EmployeeTypeEnum.System, value: "System" },
                    { key: EmployeeTypeEnum.Unknown, value: "Unknown" }
                ],
                required: true,
            },
            placeholderText: "Select employee type",
            setValue: (user: AdminUserModel, val: number) => { user.employeeType = val; }
        },
        {
            name: "companyName",
            heading: "Company",
            className: "Company",
            formComponent: LabelField,
            showInGrid: 6,
            filterable: true
        },
        {
            name: "jobTitle",
            heading: "Title",
            showInGrid: 8,
            filterable: true,
            className: "string-col",
            formComponent: LabelField
        },
        {
            name: "roleName",
            heading: "Role",
            showInGrid: 9,
            className: "string-col",
            formComponent: LabelField,
            filterable: true,
            readonly: true
        },
        {
            // this on shows in the grid
            name: "supervisor", 
            heading: "Supervisor",
            filterable: true,
            showInGrid: 7,
            className: "user-name",            
            format: (val: IUserSearchResult) => {
                return val ? val.fullname : "";
            }
        },
        {
            // this on shows in the form
            name: "supervisor",
            heading: "Supervisor",
            className: "user-id",
            formComponent: UserField,
            readonlyIf: isContractor,
            placeholderText: "Select supervisor",
            setValue: (user: AdminUserModel, val: IUserSearchResult) => {
                user.supervisor = val;
            }
        },        
        {
            name: "city",
            heading: "City",
            filterable: true,
            showInGrid: 10,
            className: "string-col",
            formComponent: LabelField
        },
        {
            name: "officeLocation",
            heading: "Location",
            filterable: true,
            showInGrid: 11,
            className: "string-col",
            formComponent: LabelField
        },
        {
            name: "department",
            heading: "Department",
            filterable: true,
            showInGrid: 12,
            className: "string-col",
            formComponent: LabelField
        },
        {
            name: "division",
            heading: "Division",
            filterable: true,
            className: "string-col",
            formComponent: LabelField
        },
        {
            name: "status",
            heading: "Status",
            filterable: true,
            showInGrid: 3,
            className: "string-col",
            formComponent: ComboField,
            readonlyIf: (user: AdminUserModel) => { return isEmployee(user) || user.status === UserStatusEnum.Inacitve },
            validation: {
                values: [
                    { key: UserStatusEnum.Active, value: "Active" },
                    { key: UserStatusEnum.Inacitve, value: "Inactive" }
                ],
                required: true,
                tip: "status is mandatory"
            },
            placeholderText: "Select a status",
            setValue: (user: AdminUserModel, val: UserStatusEnum) => { user.status = val; }
        },
        {
            name: "domainDisabled",
            heading: "Domain status",
            validation: {
                values: [
                    { key: true, value: "Disabled" },
                    { key: false, value: "Enabled" }

                ],
            },
            filterable: true,
            className: "boolean-col"
        }
    ]
} as IApplicationModule;